"use client";

import React from "react";
import { Fingerprint, Brain, Wrench, Users, ShieldCheck, Database, Terminal, Activity } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button";
const coreComponents = [{
  name: "Identity",
  health: 100,
  description: "Cryptographic verification and role-based access control for all incoming commands and operator sessions.",
  icon: Fingerprint
}, {
  name: "Soul",
  health: 98,
  description: "Persistent persona and context management engine, ensuring consistent agent behavior across sessions.",
  icon: Brain
}, {
  name: "Tools",
  health: 95,
  description: "Extensible execution environment for local scripts, API integrations, and system-level macOS commands.",
  icon: Wrench
}, {
  name: "Users",
  health: 100,
  description: "Multi-tenant isolation and profile management for distinct operator and end-user environments.",
  icon: Users
}, {
  name: "Permissions",
  health: 100,
  description: "Granular, policy-driven authorization matrix governing tool access and memory read/write operations.",
  icon: ShieldCheck
}, {
  name: "Memory",
  health: 92,
  description: "Vectorized local state storage and retrieval system for long-term context retention and fast recall.",
  icon: Database
}];
export default function CoreArchitecture() {
  return <section id="core-architecture" className="dark bg-background text-foreground py-24 px-6 md:px-12 lg:px-24 overflow-hidden relative">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
        
        {}
        <div className="space-y-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/50 border border-border/50 text-sm font-mono text-muted-foreground mb-4">
            <Activity className="size-4 text-primary" />
            <span>System Overview</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
            Core Architecture
          </h2>
          
          <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
            <p>
              qOS operates as a secure, local-first macOS agent platform. It is engineered from the ground up to securely process iMessage-native command workflows while maintaining strict data sovereignty on dedicated hardware or custom operator deployments.
            </p>
            <p>
              The framework is built on six foundational pillars. This architecture ensures every action is authenticated, contextualized, and executed strictly within defined permission boundaries, eliminating the risks associated with cloud-dependent agent systems.
            </p>
          </div>

          <div className="pt-4">
            <Button variant="outline" className="font-mono tracking-wide border-primary/30 hover:bg-primary/10 hover:text-primary transition-colors">
              <Terminal className="size-4 mr-2" />
              Initialize_Documentation
            </Button>
          </div>
        </div>

        {}
        <div className="relative">
          {}
          <div className="absolute -top-4 -left-4 w-8 h-8 border-t-2 border-l-2 border-primary/40 rounded-tl-lg" />
          <div className="absolute -bottom-4 -right-4 w-8 h-8 border-b-2 border-r-2 border-primary/40 rounded-br-lg" />

          <Card className="bg-card/30 backdrop-blur-xl border-border/40 shadow-2xl shadow-black/60 overflow-hidden rounded-xl">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent pointer-events-none" />
            
            <CardHeader className="border-b border-border/30 bg-background/40 pb-4 relative z-10 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="font-mono text-xs md:text-sm text-muted-foreground flex items-center gap-2 uppercase tracking-widest">
                <Terminal className="size-4" />
                System_Blueprint.sys
              </CardTitle>
              <div className="flex gap-1.5">
                <div className="size-2.5 rounded-full bg-destructive/80" />
                <div className="size-2.5 rounded-full bg-chart-2/80" />
                <div className="size-2.5 rounded-full bg-primary/80" />
              </div>
            </CardHeader>
            
            <CardContent className="p-6 relative z-10 space-y-3">
              {coreComponents.map((item, i) => <div key={item.name} data-index={i}>
                  <HoverCard openDelay={100} closeDelay={100}>
                    <HoverCardTrigger asChild>
                      <div className="group flex items-center justify-between p-3 rounded-lg hover:bg-white/5 border border-transparent hover:border-border/40 transition-all cursor-crosshair">
                        <div className="flex items-center gap-4">
                          <div className="p-2 rounded-md bg-background/50 border border-border/30 group-hover:border-primary/30 group-hover:bg-primary/10 transition-colors">
                            <item.icon className="size-4 text-muted-foreground group-hover:text-primary transition-colors" />
                          </div>
                          <span className="font-mono text-sm tracking-wider text-foreground group-hover:text-primary transition-colors">
                            {item.name}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-4 w-[120px] md:w-[160px]">
                          <Progress value={item.health} className="h-1.5 bg-background border border-border/50" />
                          <span className="font-mono text-xs text-muted-foreground w-10 text-right group-hover:text-foreground transition-colors">
                            {item.health}%
                          </span>
                        </div>
                      </div>
                    </HoverCardTrigger>
                    
                    <HoverCardContent side="left" align="center" sideOffset={20} className="w-80 bg-popover/95 backdrop-blur-2xl border-border/50 shadow-2xl shadow-black/80 rounded-xl">
                      <div className="space-y-3">
                        <h4 className="font-mono text-sm font-semibold text-primary flex items-center gap-2 border-b border-border/40 pb-2">
                          <item.icon className="size-4" />
                          {item.name}_Module.exe
                        </h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {item.description}
                        </p>
                        <div className="pt-2 mt-2 border-t border-border/30 flex justify-between items-center bg-background/50 p-2 rounded-md">
                           <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">Status: Active</span>
                           <span className="text-[10px] font-mono text-primary uppercase tracking-wider flex items-center gap-1">
                             <Activity className="size-3" />
                             Optimal
                           </span>
                        </div>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </div>)}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}